import React from 'react';
import './TerraceConstructionReport.scss';
import usdaLogoName from '../../../assets/images/usda-logo-name.png';
import crossSectionGenericTemp from '../../../assets/images/crossSectionGenericTemp.png';

import { formatMonthDayYear } from 'cd-eft/utils/formatDate';
import { convertBytesToBase64Url } from '../../../utils/imageUtils';
import { toJS } from 'mobx';
import { smartFormatDecimal } from 'cd-eft/utils/decimalFormatting';

// Helper function to format Drain/Block ID for two lines
export const formatCellContent = (drainId: string | null, blockId: string | null): React.ReactNode => {
  let content = drainId || blockId || '';
  if (!content) return '';
  content = content.replace(/_/g, ' ');
  // Split into words
  const words = content.split(' ');
  if (words.length > 1) {
    // Join words with <br /> for line break, handling cases with more than 2 words if necessary
    // This example splits after the first word, adjust logic if needed for longer phrases
    return (
      <>
        {words[0]}
        <br />
        {words.slice(1).join(' ')}
      </>
    );
  }
  return content;
};

interface ITerraceProfileItem {
  stationId: string;
  drainId: string | null;
  blockId: string | null;
  referenceElevation: number;
  cutFill: number;
  channelElevation: number;
  ridgeElevation: number;
  planRidgeHeight: number;
  designHeight: number;
  groundElevation: number;
}
interface ITerraceConstructionReportProps {
  terraceContent: {
    reportDate?: string;
    terraceName: string;
    planView?: {
      legend?: Array<{
        label: string;
        symbol: string;
      }>;
    };
    alignedElementDesignParamsId?: number;
    terraceType?: string;
    projectName?: string;
    projectDescription?: string | null;
    projectOwner?: string;
    projectState?: string;
    projectCounty?: string;
    practice?: string;
    designedBy?: string;
    totalCut?: number;
    totalFill?: number;
    terraceLength?: number;
    clearing?: number | string;
    other?: string;
    strippingVolume?: number;
    strippingDepth?: number;
    designWaterElevation?: number;
    flatLineLoc?: string;
    terracePlanViewImage?: string;
    locationMapImage?: string;
    image811?: number[];
    additionalInformation?: string | null;
    stationIdXsectionMap?: object;
    backsight?: number;
    heightOfInstrument?: number;
    foresight?: number;
    benchmarkElevation?: number;
    benchmarkDescription?: string;
    constructBenchmark?: string;
    onCallNumber?: string;
    terraceSummaryInfoList?: Array<{
      terraceName: string;
      terraceTemplateName: string;
      cutSlope: number | string;
      bottomWidth: number | string;
      frontSlope: number | string;
      topWidth: number | string;
      backSlope: number | string;
      fillVolume: number | string;
      excavationVolume: number | string;
      layoutLength: number | string;
    }>;
    terraceProfileList?: ITerraceProfileItem[]; // Use the interface here
    status?: string;
    isUGOConstructionReport?: boolean;
  };
  project: {
    name: string;
    location: string;
    practice: string;
    description: string;
    client?: string;
    county?: string;
    state?: string;
  };
  isUGOSelected?: boolean;
  areIndividualTerracesSelected?: boolean;
  ugoData?: any[];
  renderMode?: 'full' | 'shared-pages-only' | 'individual-pages-only';
  designIndex?: number;
  totalDesigns?: number;
}

export const TerraceConstructionReport: React.FC<ITerraceConstructionReportProps> = ({
  terraceContent,
  project,
  isUGOSelected = false,
  areIndividualTerracesSelected = false,
  ugoData = [],
  renderMode = 'full',
  designIndex = 0,
  totalDesigns = 1
}) => {
  const plainImage811Array = toJS(terraceContent.image811);
  const call811ImageUrl = convertBytesToBase64Url(plainImage811Array);
  const isUGOConstructionReport = terraceContent.isUGOConstructionReport === true; 
  const shouldShowUGOPages = isUGOConstructionReport || (isUGOSelected && !areIndividualTerracesSelected);

  // Helper function to check if we have a valid image
  const hasValidImage = (base64Image: string | undefined): boolean => {
    return !!(base64Image && (base64Image.startsWith('data:image/') || base64Image.length > 0));
  };

  const getImageSrc = (base64Image: string | undefined): string | null => {
    if (base64Image) {
      if (base64Image.startsWith('data:image/')) {
        return base64Image;
      }
      return `data:image/png;base64,${base64Image}`;
    }
    return null;
  };

  // Get dynamic image sources
  const locationImageSrc = getImageSrc(terraceContent.locationMapImage);
  const planViewImageSrc = getImageSrc(terraceContent.terracePlanViewImage);
  const hasLocationImage = hasValidImage(terraceContent.locationMapImage);
  const hasPlanViewImage = hasValidImage(terraceContent.terracePlanViewImage);

  const ReportHeader: React.FC = () => {
    const today = formatMonthDayYear(new Date());

    return (
      <div className="terrace-report__header">
        <div className="terrace-report__branding">
          <img
            id="usda-logo"
            src={usdaLogoName}
            alt="usda logo"
            className="terrace-report__logo"
            style={{ width: '500px', height: 'auto' }}
          />
        </div>
        <div className="terrace-report__date">
          <div className="terrace-report__date-label">Report Generated by <strong>NRCS - NETS</strong></div>
          <div className="terrace-report__date-value">{terraceContent.reportDate ? formatMonthDayYear(new Date(terraceContent.reportDate)) : formatMonthDayYear(new Date())}</div>
        </div>
      </div>
    );
  };

  const ReportFooter: React.FC = () => (
    <div className="terrace-report__footer">
      <div className="terrace-report__project-info">
        <div className="terrace-report__info-item">
          <strong>Project Name:</strong> {terraceContent.projectName}
        </div>
        <div className="terrace-report__info-item">
          <strong>Location:</strong> {terraceContent.projectCounty}
        </div>
        <div className="terrace-report__info-item">
          <strong>Practice:</strong> {terraceContent.practice}
        </div>
        <div className="terrace-report__info-item">
          <strong>Description:</strong> {terraceContent.projectDescription}
        </div>
      </div>
      <div className="terrace-report__signatures">
        <div className="terrace-report__signature-grid">
          <div className="terrace-report__signature-block">
            <div className="terrace-report__signature-name">
              <strong>Designed By:</strong> {terraceContent.designedBy}
            </div>
            <div className="terrace-report__signature-date">
              Date:
              <span className="terrace-report__signature-line-container">
                <span className="terrace-report__signature-underline"></span>
              </span>
            </div>
          </div>

          <div className="terrace-report__signature-block">
            <div className="terrace-report__signature-name">
              <strong>Checked By:</strong>
              <span className="terrace-report__signature-line-container">
                <span className="terrace-report__signature-underline"></span>
              </span>
            </div>
            <div className="terrace-report__signature-date">
              Date:
              <span className="terrace-report__signature-line-container">
                <span className="terrace-report__signature-underline"></span>
              </span>
            </div>
          </div>

          <div className="terrace-report__signature-block">
            <div className="terrace-report__signature-name">
              <strong>Approved By:</strong>
              <span className="terrace-report__signature-line-container">
                <span className="terrace-report__signature-underline"></span>
              </span>
            </div>
            <div className="terrace-report__signature-date">
              Date:
              <span className="terrace-report__signature-line-container">
                <span className="terrace-report__signature-underline"></span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const ReportPage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="terrace-report__page">
      <div className="terrace-report__content">
        <div className="terrace-report__container-outer-border">
          <ReportHeader />
          {children}
          <ReportFooter />
        </div>
      </div>
    </div>
  );

  // --- Use dynamic Terrace Summary Data ---
  const terraceSummaryData = terraceContent.terraceSummaryInfoList || [];


  const SUMMARY_ROWS_PER_PAGE = 19; // How many rows fit visually on one page before table breaks
  const SUMMARY_ROW_THRESHOLD = 13; // Logic threshold for placing Addtl Info

  // --- Use dynamic Profile Data ---
  const dynamicProfileData = terraceContent.terraceProfileList || []
  const PROFILE_ROWS_PER_PAGE = 3

  // --- Render Functions ---
  const renderTerraceSummaryRows = (dataChunk) => {
    return dataChunk.map((row, index) => (
      <tr key={row.terraceName || `summary-row-${index}`}>
        <td><strong>{row.terraceName}</strong></td>
        <td>{row.terraceTemplateName}</td>
        <td>{row.cutSlope}</td>
        <td>{row.bottomWidth}</td>
        <td>{row.frontSlope}</td>
        <td>{row.topWidth}</td>
        <td>{row.backSlope}</td>
        <td>{row.fillVolume.toFixed(1)}</td>
        <td>{row.excavationVolume.toFixed(1)}</td>
        <td>{row.layoutLength.toFixed(1)}</td>
      </tr>
    ));
  };

  const renderProfileTableRows = (dataChunk: ITerraceProfileItem[]) => {
    return dataChunk.map((row) => (
      <tr key={row.stationId}>
        <td><strong>{row.stationId}</strong></td>
        <td>{/* HI - Not in API */}</td>
        <td>{formatCellContent(row.drainId, row.blockId)}</td>
        <td>{row.referenceElevation?.toFixed(1)}</td>
        <td>{row.cutFill?.toFixed(1)}</td>
        <td>{row.channelElevation?.toFixed(1)}</td>
        <td>{/* Chan Grade Rod - Not in API */}</td>
        <td>{/* Act Chan Rod - Not in API */}</td>
        <td>{/* Chan Error - Not in API */}</td>
        <td>{row.planRidgeHeight?.toFixed(1)}</td>
        <td>{row.ridgeElevation?.toFixed(1)}</td>
        <td>{/* Ridge Grade Rod - Not in API */}</td>
        <td>{/* Act Ridge Rod - Not in API */}</td>
        <td>{/* Ridge Error - Not in API */}</td>
      </tr>
    ));
  };
  // --- Logic Flags ---
  const summaryRowCount = terraceSummaryData.length;
  const summaryNeedsPagination = summaryRowCount > SUMMARY_ROWS_PER_PAGE;
  const addtlInfoOnSummaryPage = summaryRowCount < SUMMARY_ROW_THRESHOLD; // Option 1 trigger
  const addtlInfoWithCrossSection = !addtlInfoOnSummaryPage; // Option 2 trigger
  // --- Profile Pagination Logic ---
  const profileRowCount = dynamicProfileData.length;
  const profileNeedsPagination = profileRowCount > PROFILE_ROWS_PER_PAGE;

  // --- Helper Component for Addtl Info & Cross Section ---
  const AdditionalInfoSection: React.FC = () => (
    <div className="terrace-report__additional-info">
      <h2 className="terrace-report__section-title">Additional Information</h2>
      <div className="terrace-report__writing-area">
        <div className="terrace-report__line"></div>
        <div className="terrace-report__line"></div>
        <div className="terrace-report__line"></div>
        <div className="terrace-report__line"></div>
        <div className="terrace-report__line"></div>
        <div className="terrace-report__line"></div>
      </div>
    </div>
  );

  const CrossSectionDiagram: React.FC = () => (
    <div className="terrace-report__cross-section">
      <h2 className="terrace-report__section-title">Generic Template Cross Section Diagram</h2>
      <img
        src={crossSectionGenericTemp}
        alt="Cross section diagram"
        className="terrace-report__diagram"
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
          margin: 0,
          objectFit: 'cover'
        }}
      />
    </div>
  );
  /* ------------------------------------------------------------------ */
  /*  UGO CONSTRUCTION CHECKOUT SHEET                                   */
  /* ------------------------------------------------------------------ */
  const UgoCheckoutSheet: React.FC<{
    ugoName: string;
    project: ITerraceConstructionReportProps["project"];
    ugoData: any[];
  }> = ({ ugoName, project, ugoData }) => {
    const ugo = ugoData && ugoData.length > 0 ? ugoData[0] : null;
    
    // UGO Profile pagination logic
    const UGO_PROFILE_ROWS_PER_PAGE = 4; // How many station/pipe pairs fit on one page
    const ugoStationRows = ugo?.stationRows || [];
    const ugoPipeRows = ugo?.pipeRows || [];
    const ugoProfileRowCount = ugoStationRows.length;
    const ugoProfileNeedsPagination = ugoProfileRowCount > UGO_PROFILE_ROWS_PER_PAGE;
    
    const renderUgoProfileRows = (stationChunk: any[], pipeChunk: any[], startIndex: number) => {
      return stationChunk.map((station: any, index: number) => {
        const actualIndex = startIndex + index;
        return (
          <React.Fragment key={`station-${actualIndex}`}>
            <tr>
              <td><strong>{station.stationId || ''}</strong></td>
              <td>{station.fixtureId || ''}</td>
              <td>{station.originalGroundElevation ? smartFormatDecimal(station.originalGroundElevation, 1) : ''}</td>
              <td>{station.planFlowlineElevation ? smartFormatDecimal(station.planFlowlineElevation, 1) : ''}</td>
              <td>{station.planCut ? smartFormatDecimal(station.planCut, 1) : ''}</td>
              <td colSpan={2} style={{ textAlign: 'left' }}>
                BM: <span className="ugo-checkout__signature-line ugo-checkout__signature-line--bm"></span><br />
                BS: <span className="ugo-checkout__signature-line ugo-checkout__signature-line--bs"></span>
              </td>
              <td>{station.hi ? smartFormatDecimal(station.hi, 1) : ''}</td>
              <td>{station.planOrificeElevation ? smartFormatDecimal(station.planOrificeElevation, 1) : ''}</td>
              <td>{station.planRiserPerfElevation ? smartFormatDecimal(station.planRiserPerfElevation, 1) : ''}</td>
              <td>&nbsp;</td>
              <td>&nbsp;</td>
              <td>&nbsp;</td>
            </tr>
            {pipeChunk[index] && (
              <tr>
                <td><strong>Pipe</strong></td>
                <td colSpan={4} style={{ textAlign: 'center' }}>
                  {pipeChunk[index].length ? smartFormatDecimal(pipeChunk[index].length, 1) : ''} ft of {pipeChunk[index].diameter || ''} in diam.<br />
                  {pipeChunk[index].material || ''}
                </td>
                <td>{pipeChunk[index].pipeGradePercent ? smartFormatDecimal(pipeChunk[index].pipeGradePercent, 2) : ''}</td>
                <td>&nbsp;</td>
                <td colSpan={6} style={{ textAlign: 'left' }}>
                  Length = {pipeChunk[index].length ? 
                    `${smartFormatDecimal(pipeChunk[index].length, 1)}(ft)` : 
                    <span className="ugo-checkout__signature-line ugo-checkout__signature-line--length"></span>
                  }&nbsp;&nbsp;
                  Diameter = {pipeChunk[index].diameter ? 
                    `${pipeChunk[index].diameter}(in)` : 
                    <span className="ugo-checkout__signature-line ugo-checkout__signature-line--diameter"></span>
                  }<br />
                  Material = {pipeChunk[index].material || 
                    <span className="ugo-checkout__signature-line ugo-checkout__signature-line--material"></span>
                  }
                </td>
              </tr>
            )}
          </React.Fragment>
        );
      });
    };
    
    return (
      <div className="ugo-checkout">
        {/* Main title */}
        <h1 className="ugo-checkout__title">UGO Construction Checkout Sheet</h1>
        <h2 className="ugo-checkout__subtitle">{ugoName}</h2>

        {/* Certification statement */}
        <p className="ugo-checkout__note">
          The construction checkout information shown on this sheet is a true
          representation of the actual construction performed. This practice was installed in accordance with Natural Resources
          Conservation Service Plans and Specifications.
        </p>

        {/* Surveyed / Certified lines */}
        <div className="terrace-report__certification-signature">
          <div className="terrace-report__certification-name">
            <strong>Surveyed By:</strong>
            <span className="terrace-report__signature-line-container">
              <span className="terrace-report__signature-underline"></span>
            </span>
          </div>
          <div className="terrace-report__certification-date">
            <strong>Date:</strong>
            <span className="terrace-report__signature-line-container">
              <span className="terrace-report__signature-underline"></span>
            </span>
          </div>
        </div>
        <div className="terrace-report__certification-signature">
          <div className="terrace-report__certification-name">
            <strong>Certified By:</strong>
            <span className="terrace-report__signature-line-container">
              <span className="terrace-report__signature-underline"></span>
            </span>
          </div>
          <div className="terrace-report__certification-date">
            <strong>Date:</strong>
            <span className="terrace-report__signature-line-container">
              <span className="terrace-report__signature-underline"></span>
            </span>
          </div>
        </div>

        {/* Profile title + glossary */}
        <h3 className="ugo-checkout__section-title">{ugoName} Profile</h3>
        <p className="ugo-checkout__glossary">
          <strong>Abbreviation Glossary:</strong>&nbsp;
          Orig = Original, Perf = Perforated, HI = Height of Instrument,
          Diam = Diameter, Len = Length, Chan = Channel, BM = Benchmark,
          BS = Backslope
        </p>

        {/* UGO PROFILE TABLE */}
        <table className="ugo-checkout__table">
          <thead>
            <tr>
              <th>Station</th>
              <th>Fixture<br />ID</th>
              <th>Orig.<br />Ground<br />Elev.<br />(ft)</th>
              <th>Plan<br />Flowline<br />Elev.<br />(ft)</th>
              <th>Plan<br />Cut<br />(ft)</th>
              <th>Plan<br />Pipe<br />Grade<br />(%)</th>
              <th>As-<br />Built<br />Pipe<br />Grade<br />(%)</th>
              <th>HI</th>
              <th>Plan<br />Orifice<br />Elev.<br />(ft)</th>
              <th>Plan<br />Riser<br />Perf.<br />Elev.<br />(ft)</th>
              <th>As-Built<br />Orifice<br />Rod/<br />Elev.</th>
              <th>As-Built<br />Riser<br />Perf.<br />Rod/<br />Elev.</th>
              <th>As-<br />Built<br />Flowline<br />Rod/<br />Elev.</th>
            </tr>
          </thead>
          <tbody>
            {/* Render station rows from API data with pagination */}
            {ugo && ugoStationRows.length > 0 && (
              renderUgoProfileRows(
                ugoProfileNeedsPagination 
                  ? ugoStationRows.slice(0, UGO_PROFILE_ROWS_PER_PAGE)
                  : ugoStationRows,
                ugoProfileNeedsPagination 
                  ? ugoPipeRows.slice(0, UGO_PROFILE_ROWS_PER_PAGE)
                  : ugoPipeRows,
                0
              )
            )}
          </tbody>
        </table>
      </div>
    );
  };

  /* ------------------------------------------------------------------ */
  /*  UGO INLET/OUTLET TABLES PAGE                                      */
  /* ------------------------------------------------------------------ */
  const UgoInletOutletPage: React.FC<{
    ugoName: string;
    ugoData: any[];
  }> = ({ ugoName, ugoData }) => {
    // Get the first UGO data item for rendering (assuming one UGO per report)
    const ugo = ugoData && ugoData.length > 0 ? ugoData[0] : null;
    
    return (
      <div className="ugo-checkout">

        {/* INLET TABLE */}
        <h3 className="ugo-checkout__section-title">{ugoName} Inlet</h3>
        <table className="ugo-checkout__table" style={{ marginBottom: '2rem' }}>
          <thead>
            <tr>
              <th>Inlet ID</th>
              <th>Chan.<br />Bottom<br />Elev</th>
              <th>Chan.<br />Cut</th>
              <th>Inlet<br />Diam.<br />(in)</th>
              <th>Perf.<br />Size<br />(in)</th>
              <th>Holes /<br />Foot</th>
              <th>Perf.<br />Length<br />(ft)</th>
              <th>Guard</th>
              <th>Orifice<br />Elev.</th>
              <th>Orifice<br />Diam.<br />(in)</th>
              <th>Base /<br />Elbow<br />Elev.</th>
              <th>Offset<br />Pipe<br />Diam.<br />(in)</th>
              <th>Offset<br />Pipe<br />Length<br />(ft)</th>
            </tr>
          </thead>
          <tbody>
            {/* Render inlet rows from API data */}
            {ugo && ugo.inletRows && ugo.inletRows.map((inlet: any, index: number) => (
              <React.Fragment key={`inlet-${index}`}>
                <tr>
                  <td><strong>{inlet.inletId || `Inlet ${index + 1}`}</strong></td>
                  <td>{inlet.channelBottomElevation ? smartFormatDecimal(inlet.channelBottomElevation, 2) : ''}</td>
                  <td>{inlet.channelCut ? smartFormatDecimal(inlet.channelCut, 1) : ''}</td>
                  <td>{inlet.inletDiameter || ''}</td>
                  <td>{inlet.perforationSize || ''}</td>
                  <td>{inlet.holesPerFoot || ''}</td>
                  <td>{inlet.perforationLength || ''}</td>
                  <td>{inlet.guard || ''}</td>
                  <td>{inlet.orificeElevation ? smartFormatDecimal(inlet.orificeElevation, 2) : ''}</td>
                  <td>{inlet.orificeDiameter || ''}</td>
                  <td>{inlet.baseElbowElevation ? smartFormatDecimal(inlet.baseElbowElevation, 1) : ''}</td>
                  <td>{inlet.offsetPipeDiameter || '-'}</td>
                  <td>{inlet.offsetPipeLength || '-'}</td>
                </tr>
                <tr>
                  <td><strong>As-Built</strong></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>

        {/* OUTLET TABLE */}
        <h3 className="ugo-checkout__section-title">{ugoName} Outlet</h3>
        <table className="ugo-checkout__table" style={{ marginBottom: '2rem' }}>
          <thead>
            <tr>
              <th>Outlet ID</th>
              <th>Outlet<br />Type</th>
              <th>Outlet<br />Diam.<br />(in)</th>
              <th>Length<br />or<br />Height (ft)</th>
              <th>Holes /<br />Foot</th>
              <th>Perf.<br />Size<br />(in)</th>
              <th>Guard</th>
              <th>Outlet<br />Elev.</th>
              <th style={{ width: '200px' }}>Material</th>
            </tr>
          </thead>
          <tbody>
            {/* Render outlet rows from API data */}
            {ugo && ugo.outletRows && ugo.outletRows.map((outlet: any, index: number) => (
              <React.Fragment key={`outlet-${index}`}>
                <tr>
                  <td><strong>{outlet.outletId || `Outlet ${index + 1}`}</strong></td>
                  <td>{outlet.outletType || ''}</td>
                  <td>{outlet.outletDiameter || ''}</td>
                  <td>{outlet.lengthOrHeight || ''}</td>
                  <td>{outlet.holesPerFoot || '-'}</td>
                  <td>{outlet.perforationSize || '-'}</td>
                  <td>{outlet.guard || ''}</td>
                  <td>{outlet.outletElevation ? smartFormatDecimal(outlet.outletElevation, 1) : ''}</td>
                  <td>{outlet.material || ''}</td>
                </tr>
                <tr>
                  <td><strong>As-Built</strong></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const shouldRenderSharedPages = renderMode === 'full' || renderMode === 'shared-pages-only';
  const shouldRenderIndividualPages = renderMode === 'full' || renderMode === 'individual-pages-only';
  const shouldShowTerraceCheckoutSheet = shouldRenderIndividualPages && !isUGOConstructionReport;
  

  return (
    <div className="terrace-report">

      {/* SHARED PAGES - Rendered once per combined report */}
      {shouldRenderSharedPages && (
        <>
          {/* First Page - Location View */}
          <ReportPage>
            <h1 className="terrace-report__title">Terrace Construction Report</h1>
            <div className="terrace-report__location-view">
              <h2 className="terrace-report__location-title">Location View</h2>
              <div className="terrace-report__map-container">
                {hasLocationImage && locationImageSrc ? (
                  <img
                    src={locationImageSrc}
                    alt="Location view"
                    className="terrace-report__location-map"
                  />
                ) : (
                  <p className="terrace-report__image-message">
                    The image has not been generated at this time. Users have the option to upload their own images by utilizing the toolbar feature. This allows for customization and ensures that the final output meets individual preferences and requirements.
                  </p>
                )}
              </div>
            </div>

            <div className="terrace-report__details">
              <h2 className="terrace-report__section-title">Details</h2>

            </div>

            <div className="terrace-report__call-info">
              <img
                src={call811ImageUrl}
                alt="Call 811 Logo"
                className="terrace-report__call-icon"
              />
              <p className="terrace-report__call-text terrace-report__call-text--subscript">
                {terraceContent.onCallNumber}
              </p>
            </div>

            <div className="terrace-report__certification">
              <p className="terrace-report__certification-text">
                I certify the information recorded on this sheet is a true representation of the actual practice
                installation and the practice as installed does (    ) or does not (    ) meet NRCS plans and specifications.
              </p>

              <div className="terrace-report__certification-signature">
                <div className="terrace-report__certification-name">
                  <strong>Certified/ Surveyed By:</strong>
                  <span className="terrace-report__signature-line-container">
                    <span className="terrace-report__signature-underline"></span>
                  </span>
                </div>
                <div className="terrace-report__certification-date">
                  <strong>Date:</strong>
                  <span className="terrace-report__signature-line-container">
                    <span className="terrace-report__signature-underline"></span>
                  </span>
                </div>
              </div>
            </div>
          </ReportPage>

          {/* Second Page - Plan View */}
          <ReportPage>
            <div className="terrace-report__plan-view">
              <h2 className="terrace-report__section-title terrace-report__section-title--align-left">Plan View</h2>
              <div className="terrace-report__map-container">
                {hasPlanViewImage && planViewImageSrc ? (
                  <img
                    src={planViewImageSrc}
                    alt="Plan View"
                    className="terrace-report__mapp"
                  />
                ) : (
                  <p className="terrace-report__image-message">
                    The image has not been generated at this time. Users have the option to upload their own images by utilizing the toolbar feature. This allows for customization and ensures that the final output meets individual preferences and requirements.
                  </p>
                )}
              </div>
            </div>
          </ReportPage>

          {/* Third Page - Terrace Summary */}
          <ReportPage>
            <div className="terrace-report__terrace-summary">
              <h2 className="terrace-report__section-title">Terrace Summary</h2>
              <div className="terrace-report__table">
                <table>
                  <thead>
                    <tr>
                      <th>Terrace</th>
                      <th>Template</th>
                      <th>Cut Slope</th>
                      <th>Bottom Width</th>
                      <th>Front Slope</th>
                      <th>Top Width</th>
                      <th>Back Slope</th>
                      <th>Fill Volume</th>
                      <th>Excavation Volume</th>
                      <th>Layout Length</th>
                    </tr>
                  </thead>
                  <tbody>
                    {renderTerraceSummaryRows(
                      summaryNeedsPagination
                        ? terraceSummaryData.slice(0, SUMMARY_ROWS_PER_PAGE)
                        : terraceSummaryData
                    )}
                  </tbody>
                </table>
              </div>
              {/* OPTION 1: Render Addtl Info here ONLY if rows < threshold AND table fits on this page */}
              {addtlInfoOnSummaryPage && !summaryNeedsPagination && (
                <AdditionalInfoSection />
              )}
            </div>
          </ReportPage>

          {/* Page 3b (Optional) - Remaining Summary Rows */}
          {summaryNeedsPagination && (
            <ReportPage>
              {/* Render remaining summary rows */}
              <div className="terrace-report__terrace-summary" style={{ marginTop: '0.75rem' }}>
                <div className="terrace-report__table">
                  <table>
                    <thead>
                      <tr>
                        <th>Terrace</th>
                        <th>Template</th>
                        <th>Cut Slope</th>
                        <th>Bottom Width</th>
                        <th>Front Slope</th>
                        <th>Top Width</th>
                        <th>Back Slope</th>
                        <th>Fill Volume</th>
                        <th>Excavation Volume</th>
                        <th>Layout Length</th>
                      </tr>
                    </thead>
                    <tbody>
                      {renderTerraceSummaryRows(terraceSummaryData.slice(SUMMARY_ROWS_PER_PAGE))}
                    </tbody>
                  </table>
                </div>
              </div>
              {/* OPTION 1: Render Addtl Info here ONLY if rows < threshold AND table needed pagination */}
              {addtlInfoOnSummaryPage && (
                <AdditionalInfoSection />
              )}
            </ReportPage>
          )}

          {/* Page 3c (Conditional) - Addtl Info (maybe) + Cross Section */}
          {/* This page always contains at least the Cross Section */}
          <ReportPage>
            <div style={{ marginTop: '0.75rem' }}> {/* Add margin for alignment if needed */}
              {/* OPTION 2: Render Addtl Info here if rows >= threshold */}
              {addtlInfoWithCrossSection && (
                <AdditionalInfoSection />
              )}
              {/* Always render Cross Section on this page */}
              <CrossSectionDiagram />
            </div>
          </ReportPage>
        </>
      )}

      {/* INDIVIDUAL PAGES - Rendered per design */}
      {shouldRenderIndividualPages && (
        <>
          {/* Fourth Page - Construction Checkout Sheet (only for terrace reports, not UGO construction reports) */}
          {shouldShowTerraceCheckoutSheet && (
            <>
              <ReportPage>
                <div className="terrace-report__title">Terrace Construction Checkout Sheet</div>
                <h2 className="terrace-report__subtitle">{terraceContent.terraceName}</h2>

            <div className="terrace-report__benchmark">
              <h2 className="terrace-report__section-title">Benchmark Description</h2>
              <div className="terrace-report__benchmark-fields">
                <div className="terrace-report__field-row">
                  <div className="terrace-report__field-item">
                    Backsight: {terraceContent.backsight?.toFixed(1) ?? ''}
                  </div>
                  <div className="terrace-report__field-item">
                    Height of Instrument: {terraceContent.heightOfInstrument?.toFixed(1) ?? ''}
                  </div>
                  <div className="terrace-report__field-item">
                    Foresight: {terraceContent.foresight?.toFixed(1) ?? ''}
                  </div>
                  <div className="terrace-report__field-item">
                    BM Elevation: {terraceContent.benchmarkElevation?.toFixed(1) ?? ''}
                  </div>
                </div>
                <div className="terrace-report__field-row">
                  <div className="terrace-report__field-item">
                    Backsight:
                    <span className="terrace-report__signature-line-container">
                      <span className="terrace-report__signature-underline"></span>
                    </span>
                  </div>
                  <div className="terrace-report__field-item">
                    Height of Instrument:
                    <span className="terrace-report__signature-line-container">
                      <span className="terrace-report__signature-underline"></span>
                    </span>
                  </div>
                  <div className="terrace-report__field-item">
                    Foresight:
                    <span className="terrace-report__signature-line-container">
                      <span className="terrace-report__signature-underline"></span>
                    </span>
                  </div>
                  <div className="terrace-report__field-item">
                    Elevation:
                    <span className="terrace-report__signature-line-container">
                      <span className="terrace-report__signature-underline"></span>
                    </span>
                  </div>
                </div>
                <div className="terrace-report__field-row">
                  <div className="terrace-report__field-item">
                    Close:
                    <span className="terrace-report__signature-line-container">
                      <span className="terrace-report__signature-underline"></span>
                    </span>
                  </div>
                </div>
              </div>

              <p>{terraceContent.benchmarkDescription}</p>
            </div>
            <div className="terrace-report__earthwork">
              <h2 className="terrace-report__section-title">Earthwork</h2>
              <div className="terrace-report__earthwork-grid">
                {[
                  { label: "Stripping Volume (cy)", value: terraceContent.strippingVolume?.toFixed(1) ?? "" },
                  { label: "Total Fill", value: terraceContent.totalFill?.toFixed(1) ?? "" },
                  { label: "Total Cut (cy)", value: terraceContent.totalCut?.toFixed(1) ?? "" },
                  { label: "Total Length (ft)", value: terraceContent.terraceLength?.toFixed(1) ?? "" },
                  { label: "Stripping Depth (in)", value: terraceContent.strippingDepth?.toFixed(1) ?? "" },
                  { label: "Flagline Loc", value: terraceContent.flatLineLoc || "" },
                  { label: "Design Water Elevation", value: terraceContent.designWaterElevation?.toFixed(1) ?? "" },
                  { label: "Benchmark Description Water", value: terraceContent.constructBenchmark || "" },
                ].map((item, index) => (
                  <div key={index} className="grid-item">
                    <strong>{item.label}:</strong> {item.value}
                  </div>
                ))}
              </div>
            </div>

            {/* Profile Section (Partial Table) */}
            <div className="terrace-report__profile">
              <h2 className="terrace-report__section-title">{terraceContent.terraceName} Profile</h2>
              <p><strong>Note:</strong> User selected every 50 feet and select alignment station.</p>
              <p><strong>Abbreviation Glossary:</strong> HI = Height of Instrument, D = Drain, B = Block, Ref Elev = Ref Elevation, C/F =
                Cut/Fill, Chan = Channel, Act= Actual, Ht = Height</p>
              <div className="terrace-report__table">
                <table>
                  <thead>
                    <tr>
                      <th>Station</th>
                      <th>HI</th>
                      <th>D or B<br />ID</th>
                      <th>Ref <br /> Elev<br />(Flagline)</th>
                      <th>Chan<br />C/F<br />(ft)</th>
                      <th>Plan<br />Chan<br />Elev</th>
                      <th>Chan<br />Grade<br />Rod</th>
                      <th>Act<br />Chan<br />Rod</th>
                      <th>Chan<br />Error<br />+/-</th>
                      <th>Plan<br />Ridge<br />Ht (ft)</th>
                      <th>Ridge<br />Elev<br />(ft)</th>
                      <th>Ridge<br />Grade<br />Rod</th>
                      <th>Act<br />Ridge<br />Rod</th>
                      <th>Ridge<br />Error<br />+/-</th>
                    </tr>
                  </thead>
                  <tbody>
                    {renderProfileTableRows(
                      profileNeedsPagination
                        ? dynamicProfileData.slice(0, PROFILE_ROWS_PER_PAGE)
                        : dynamicProfileData
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </ReportPage>

          {/* Fifth Page (Optional) - Remaining Profile Table Rows */}
          {profileNeedsPagination && (
            <ReportPage>
              {/* Only render the rest of the table */}
              <div className="terrace-report__profile" style={{ marginTop: '0.75rem' }}> {/* Add margin to align with header */}
                <div className="terrace-report__table">
                  <table>
                    <thead>
                      <tr>
                        <th>Station</th>
                        <th>HI</th>
                        <th>D or B<br />ID</th>
                        <th>Ref Elev<br />(Flagline)</th>
                        <th>Chan<br />C/F<br />(ft)</th>
                        <th>Plan<br />Chan<br />Elev</th>
                        <th>Chan<br />Grade<br />Rod</th>
                        <th>Act<br />Chan<br />Rod</th>
                        <th>Chan<br />Error<br />+/-</th>
                        <th>Plan<br />Ridge<br />Ht (ft)</th>
                        <th>Ridge<br />Elev<br />(ft)</th>
                        <th>Ridge<br />Grade<br />Rod</th>
                        <th>Act<br />Ridge<br />Rod</th>
                        <th>Ridge<br />Error<br />+/-</th>
                      </tr>
                    </thead>
                    <tbody>
                      {renderProfileTableRows(dynamicProfileData.slice(PROFILE_ROWS_PER_PAGE))}
                    </tbody>
                  </table>
                </div>
              </div>
            </ReportPage>
          )}
            </>
          )}

          {/* UGO PAGES - Only show when UGO is selected and individual pages mode */}
          {shouldShowUGOPages && (
            <>
              {/* UGO Checkout Sheet */}
              <ReportPage>
                <UgoCheckoutSheet
                  ugoName={terraceContent.terraceName}
                  project={project}
                  ugoData={ugoData}
                />
              </ReportPage>

              {/* UGO Profile Continuation Pages (if needed) */}
              {ugoData && ugoData.length > 0 && ugoData[0].stationRows && ugoData[0].stationRows.length > 3 && (
                (() => {
                  const ugo = ugoData[0];
                  const UGO_PROFILE_ROWS_PER_PAGE = 4;
                  const ugoStationRows = ugo.stationRows || [];
                  const ugoPipeRows = ugo.pipeRows || [];
                  const totalRows = ugoStationRows.length;
                  const remainingRows = totalRows - UGO_PROFILE_ROWS_PER_PAGE;
                  const additionalPages = Math.ceil(remainingRows / UGO_PROFILE_ROWS_PER_PAGE);

                  return Array.from({ length: additionalPages }, (_, pageIndex) => {
                    const startIndex = UGO_PROFILE_ROWS_PER_PAGE + (pageIndex * UGO_PROFILE_ROWS_PER_PAGE);
                    const endIndex = Math.min(startIndex + UGO_PROFILE_ROWS_PER_PAGE, totalRows);
                    const stationChunk = ugoStationRows.slice(startIndex, endIndex);
                    const pipeChunk = ugoPipeRows.slice(startIndex, endIndex);

                    return (
                      <ReportPage key={`ugo-profile-page-${pageIndex + 2}-design-${designIndex}`}>
                        <div className="ugo-checkout" style={{ marginTop: '0.75rem' }}>
                          {/* UGO PROFILE TABLE - Continuation */}
                          <table className="ugo-checkout__table">
                            <thead>
                              <tr>
                                <th>Station</th>
                                <th>Fixture<br />ID</th>
                                <th>Orig.<br />Ground<br />Elev.<br />(ft)</th>
                                <th>Plan<br />Flowline<br />Elev.<br />(ft)</th>
                                <th>Plan<br />Cut<br />(ft)</th>
                                <th>Plan<br />Pipe<br />Grade<br />(%)</th>
                                <th>As-<br />Built<br />Pipe<br />Grade<br />(%)</th>
                                <th>HI</th>
                                <th>Plan<br />Orifice<br />Elev.<br />(ft)</th>
                                <th>Plan<br />Riser<br />Perf.<br />Elev.<br />(ft)</th>
                                <th>As-Built<br />Orifice<br />Rod/<br />Elev.</th>
                                <th>As-Built<br />Riser<br />Perf.<br />Rod/<br />Elev.</th>
                                <th>As-<br />Built<br />Flowline<br />Rod/<br />Elev.</th>
                              </tr>
                            </thead>
                            <tbody>
                              {stationChunk.map((station: any, index: number) => {
                                const actualIndex = startIndex + index;
                                return (
                                  <React.Fragment key={`station-continuation-${actualIndex}`}>
                                    <tr>
                                      <td><strong>{station.stationId || ''}</strong></td>
                                      <td>{station.fixtureId || ''}</td>
                                      <td>{station.originalGroundElevation ? station.originalGroundElevation.toFixed(1) : ''}</td>
                                      <td>{station.planFlowlineElevation ? station.planFlowlineElevation.toFixed(1) : ''}</td>
                                      <td>{station.planCut ? station.planCut.toFixed(1) : ''}</td>
                                      <td colSpan={2} style={{ textAlign: 'left' }}>
                                        BM: <span className="ugo-checkout__signature-line ugo-checkout__signature-line--bm"></span><br />
                                        BS: <span className="ugo-checkout__signature-line ugo-checkout__signature-line--bs"></span>
                                      </td>
                                      <td>{station.hi ? station.hi.toFixed(1) : ''}</td>
                                      <td>{station.planOrificeElevation ? station.planOrificeElevation.toFixed(1) : ''}</td>
                                      <td>{station.planRiserPerfElevation ? station.planRiserPerfElevation.toFixed(1) : ''}</td>
                                      <td>&nbsp;</td>
                                      <td>&nbsp;</td>
                                      <td>&nbsp;</td>
                                    </tr>
                                    {/* Render corresponding pipe row if available */}
                                    {pipeChunk[index] && (
                                      <tr>
                                        <td><strong>Pipe</strong></td>
                                        <td colSpan={4} style={{ textAlign: 'center' }}>
                                          {pipeChunk[index].length ? pipeChunk[index].length.toFixed(1) : ''} ft of {pipeChunk[index].diameter || ''} in diam.<br />
                                          {pipeChunk[index].material || ''}
                                        </td>
                                        <td>{pipeChunk[index].pipeGradePercent ? pipeChunk[index].pipeGradePercent.toFixed(2) : ''}</td>
                                        <td>&nbsp;</td>
                                        <td colSpan={6} style={{ textAlign: 'left' }}>
                                          Length = {pipeChunk[index].length ? 
                                            `${pipeChunk[index].length.toFixed(1)}(ft)` : 
                                            <span className="ugo-checkout__signature-line ugo-checkout__signature-line--length"></span>
                                          }&nbsp;&nbsp;
                                          Diameter = {pipeChunk[index].diameter ? 
                                            `${pipeChunk[index].diameter}(in)` : 
                                            <span className="ugo-checkout__signature-line ugo-checkout__signature-line--diameter"></span>
                                          }<br />
                                          Material = {pipeChunk[index].material || 
                                            <span className="ugo-checkout__signature-line ugo-checkout__signature-line--material"></span>
                                          }
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </ReportPage>
                    );
                  });
                })()
              )}

              {/* UGO Inlet/Outlet Tables */}
              <ReportPage>
                <UgoInletOutletPage
                  ugoName={terraceContent.terraceName}
                  ugoData={ugoData}
                />
              </ReportPage>
            </>
          )}
        </>
      )}

    </div>
  );
};